import { useState, useCallback } from 'react';
import { useCompanyStore } from '../stores/companyStore';
import { companiesAPI } from '../utils/api';
import { notify } from '../stores/notificationStore';

/**
 * Hook personnalisé pour gérer le logo de l'entreprise
 * Architecture 3 couches: Component → Hook → API Client → HTTP
 *
 * Responsabilités:
 * - Gestion de l'upload du logo
 * - Validation du fichier (taille, type)
 * - Prévisualisation du logo
 * - Gestion des erreurs
 */
export const useCompanyLogo = (companyId?: string) => {
  const { company, setCompany } = useCompanyStore();

  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string>('');

  // Taille maximale: 5MB
  const MAX_FILE_SIZE = 5 * 1024 * 1024;

  // Types d'images acceptés
  const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

  /**
   * Valide le fichier image
   */
  const validateFile = useCallback((file: File): { valid: boolean; error?: string } => {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      return {
        valid: false,
        error: 'Type de fichier non supporté. Utilisez JPG, PNG, GIF ou WebP.'
      };
    }

    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: 'Le fichier est trop volumineux. Taille maximale: 5MB.'
      };
    }

    return { valid: true };
  }, []);

  /**
   * Crée une URL de prévisualisation pour le fichier
   */
  const createPreview = useCallback((file: File) => {
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return url;
  }, []);

  /**
   * Nettoie l'URL de prévisualisation
   */
  const clearPreview = useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  }, [previewUrl]);

  /**
   * Upload le logo vers l'API
   */
  const uploadLogo = useCallback(async (file: File) => {
    if (!companyId) {
      notify.error('Aucune société sélectionnée', 'Erreur');
      return false;
    }

    // Valider le fichier
    const validation = validateFile(file);
    if (!validation.valid) {
      setError(validation.error || '');
      notify.error(validation.error || 'Fichier invalide', 'Erreur de validation');
      return false;
    }

    try {
      setUploading(true);
      setError('');

      // Créer FormData pour l'upload
      const formData = new FormData();
      formData.append('logo', file);

      // Appel API
      const response = await companiesAPI.uploadLogo(companyId, formData);

      // Mettre à jour le store avec la nouvelle URL du logo
      // Backend retourne { success, data: { ...company, logoUrl } }
      const logoUrl = response.data?.logoUrl || response.logoUrl;
      if (logoUrl) {
        if (company) {
          setCompany({ ...company, logoUrl });
        }
        notify.success('Logo mis à jour avec succès !', 'Upload réussi');

        // Nettoyer la prévisualisation après l'upload
        clearPreview();

        return true;
      }

      return false;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de l\'upload';
      setError(errorMessage);
      notify.error('Erreur : ' + errorMessage, 'Erreur d\'upload');
      return false;
    } finally {
      setUploading(false);
    }
  }, [companyId, company, validateFile, clearPreview, setCompany]);

  /**
   * Gère la sélection d'un fichier avec prévisualisation
   */
  const handleFileSelect = useCallback((file: File) => {
    const validation = validateFile(file);

    if (!validation.valid) {
      setError(validation.error || '');
      notify.error(validation.error || 'Fichier invalide', 'Erreur de validation');
      return false;
    }

    // Créer la prévisualisation
    createPreview(file);
    setError('');
    return true;
  }, [validateFile, createPreview]);

  /**
   * Supprime le logo (définit logoUrl à null)
   */
  const removeLogo = useCallback(async () => {
    if (!companyId) {
      notify.error('Aucune société sélectionnée', 'Erreur');
      return false;
    }

    try {
      setUploading(true);
      setError('');

      // Appel API pour supprimer le logo (envoie null)
      await companiesAPI.update(companyId, { logoUrl: null });

      // Mettre à jour le store
      if (company) {
        setCompany({ ...company, logoUrl: undefined });
      }

      // Nettoyer la prévisualisation
      clearPreview();

      notify.success('Logo supprimé avec succès !', 'Suppression réussie');
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la suppression';
      setError(errorMessage);
      notify.error('Erreur : ' + errorMessage, 'Erreur de suppression');
      return false;
    } finally {
      setUploading(false);
    }
  }, [companyId, company, clearPreview, setCompany]);

  return {
    // État
    uploading,
    previewUrl,
    error,

    // Actions
    uploadLogo,
    handleFileSelect,
    createPreview,
    clearPreview,
    removeLogo,
    validateFile,

    // Constantes
    MAX_FILE_SIZE,
    ACCEPTED_IMAGE_TYPES,
  };
};
